import { Box, Typography } from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import { useRouterState } from "@tanstack/react-router";

import {
  FRAME,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import { Wordmark } from "./FooterBrand";
import { Copyright } from "./FooterLegal";
import { COLUMN_TITLE, FooterLink } from "./FooterNav";
import { COMPANY_LINKS } from "./footerContent";

const TITLE_ID = "footer-company";

/** `.footer_row.is-centered`: side by side on their feet, stacked below the wide step. */
const ROW = {
  display: "flex",
  justifyContent: "space-between",
  // Stacked, the published row keeps this too, which is what sets the column
  // against the right edge between the two steps where it has no width.
  alignItems: "flex-end",
  [NOT_WIDE]: { flexDirection: "column", gap: "32px" },
  [MID]: { gap: "16px" },
} satisfies SxProps<Theme>;

/** `.fotter_menu_item`: the one column, 8 in from both sides once stacked. */
const COLUMN = {
  [NOT_WIDE]: { mx: "8px" },
  [MID]: { width: 1, mx: 0, mb: "24px" },
  [TINY]: { mb: 0 },
} satisfies SxProps<Theme>;

/** `.link-menu.is-horizontal-padding`: the links in a row, wrapping on a phone. */
const LIST = {
  display: "flex",
  gap: "32px",
  listStyle: "none",
  m: 0,
  mb: "10px",
  p: 0,
  [MID]: { alignItems: "center", mb: 0 },
  [TINY]: { flexWrap: "wrap", gap: "16px" },
} satisfies SxProps<Theme>;

/**
 * `.footer_section.is-small`: the footer a campaign page closes on instead of
 * the site's, the wordmark and the copyright beside the Company links, on a
 * band with its top corners rounded.
 */
export default function SmallFooter() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const here = pathname.replace(/\/$/, "");

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: marketingPalette.darkGround,
        border: `1px solid ${alpha("#FFFFFF", 0.06)}`,
        borderRadius: "32px 32px 0 0",
        fontSize: 16,
        lineHeight: "24px",
        [NOT_WIDE]: { borderRadius: "16px 16px 0 0" },
      }}
    >
      <Box
        sx={{
          width: 1,
          maxWidth: FRAME.footer,
          mx: "auto",
          p: "24px 36px",
          textAlign: "left",
          position: "relative",
          zIndex: 3,
          [NOT_WIDE]: { px: "24px" },
          [TINY]: { px: "12px" },
        }}
      >
        <Box sx={ROW}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              rowGap: "8px",
              maxWidth: 320,
              mr: "48px",
              [NOT_WIDE]: { width: 1, maxWidth: 1, mr: 0 },
            }}
          >
            <Wordmark flush />
            <Copyright />
          </Box>
          <Box component="nav" aria-labelledby={TITLE_ID} sx={COLUMN}>
            {/* `h6.footer_link-title`: a heading here, as the small footer publishes it. */}
            <Typography
              component="h6"
              id={TITLE_ID}
              sx={[COLUMN_TITLE, { mb: "12px" }]}
            >
              Company
            </Typography>
            <Box component="ul" sx={LIST}>
              {COMPANY_LINKS.map((item) => (
                <Box
                  component="li"
                  key={item.href}
                  sx={{ my: "6px", [MID]: { my: 0 } }}
                >
                  <FooterLink item={item} current={item.href === here} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
